import { create } from "zustand";

const SELECTION_KEY = "orgScopeUuid";

// Storage throws in private windows and embedded webviews; a school picker is
// not worth failing a page render over.
const readSelection = () => {
  try {
    return sessionStorage.getItem(SELECTION_KEY);
  } catch (_e) {
    return null;
  }
};

const writeSelection = (uuid) => {
  try {
    if (uuid) sessionStorage.setItem(SELECTION_KEY, uuid);
    else sessionStorage.removeItem(SELECTION_KEY);
  } catch (_e) {
    /* not worth failing over */
  }
};

// Org-subtree state for an org-scope viewer (head-org staff / superuser):
// which child org they are drilled into, plus the cached list of in-scope orgs
// for the selector. Shared so the selection and the list survive navigation
// across the analytics pages (dashboard → standards → detail → heatmap).
// `selectedOrgUuid === null` means "own school".
//
// The SELECTION also survives a reload, a language switch and back/forward,
// via sessionStorage — finding one school again among sixty, every time the
// page reloads, is the cost of not persisting it. Per TAB, deliberately:
// drilling into a school in one tab must not move another tab underneath
// someone. useOrgScope re-validates a restored uuid against the subtree it
// loads, so a stale or out-of-scope value is dropped rather than queried.
const useOrgScopeStore = create((set) => ({
  selectedOrgUuid: readSelection(),
  organizations: [],
  loaded: false,
  setSelectedOrgUuid: (uuid) => {
    writeSelection(uuid);
    set({ selectedOrgUuid: uuid });
  },
  setOrganizations: (orgs) => set({ organizations: orgs, loaded: true }),
  // The org NAMES are server-translated, so a language switch has to re-fetch
  // the list — but not forget which school the person picked. Keeping the
  // selection is the whole difference between a re-fetch and a reset.
  invalidateOrganizations: () => set({ organizations: [], loaded: false }),
  // Clear on user switch — the selection and cached subtree belong to one
  // session; the next user must not inherit them (see useAuth.logoutUser).
  reset: () => {
    writeSelection(null);
    set({ selectedOrgUuid: null, organizations: [], loaded: false });
  },
}));

export default useOrgScopeStore;
